import { Board } from "../board";
import { Direction, cycleDirection } from "../direction";
import { MarkovChain } from "../markovChain";
import { randomChance, randomInt } from "../random";
import { Strings } from "../strings";
import { BigSquid } from "../mobs/bigSquid";
import { ArtilleryRobot } from "../things/artilleryRobot";
import { Rock } from "../things/rock";
import { DestroyedRock } from "../things/destroyedRock";
import { ConcreteFloor } from "../things/floors/concreteFloor";
import { LevelChangerThing } from "../things/levelChangerThing";
import { LevelDown } from "../things/levelDown";
import { LevelDirector } from "../things/levelDirector";
import { LevelUp } from "../things/levelUp";
import { LootBox } from "../things/lootBox";
import { MedicalLootBox } from "../things/medicalLootBox";
import { MetalLootBox } from "../things/metalLootBox";
import { KeyLootBox } from "../things/keyLootBox";
import { Plant } from "../things/plant";
import type { MobSpawnerConfig } from "../things/portal2/mobSpawnerConfig";
import { XY } from "../xy";
import { generateWithRetries, type LevelAcceptanceCriteria } from "./levelGenerator";
import { LevelGeneratorScrewUp } from "./levelGeneratorScrewUp";
import {
	applySimplexNoise,
	breadthFirstSearch,
	getRandomXYByCriteriaInArea,
	placeNxNMultiTileThing,
	placeRandomPortal,
	populateLootBoxes,
	populateVendingMachines,
	putPrefabOnArea,
	spawnMobsWithNoise,
	spawnThings,
	assignGoldToPrePlacedMobs,
} from "./levelGeneratorUtils";
import type { LootTable } from "./lootTable";
import type { VendingTable } from "./vendingTable";
import type { MobGenerationConfig } from "./mobGenerationConfig";
import type { Prefab } from "./prefab";
import { getBigContainerPrefab } from "./prefabs/generalPrefabs";
import {
	getCaveDescent,
	getSmallBloodTemple,
	getBloodTemple,
	getGasBarrelsWithFence,
	getBuilding1,
	getMobFortress1,
} from "./prefabs/wastelandPrefabs";
import { BOARD_WIDTH, BOARD_HEIGHT } from "../gameConfig";

// Denser rocks/rubble near the play-area edges so the map feels less open.
const BORDER_ROCK_BIAS_MARGIN = 4; // tiles inward the bias reaches before fading to zero
const BORDER_ROCK_BIAS_STRENGTH = 0.2; // noise boost at the very edge
const BORDER_ROCK_BIAS_EXPONENT = 1.2; // falloff curve (1 = linear, >1 = hugs the edge)

export interface WastelandLevelParams {
	levelNumber: number;
	rng?: () => number;

	// Spawning
	numberOfPortals?: number;
	numberOfFlavourPrefabs?: number;
	numberOfBigSquids?: number;
	numberOfArtilleryRobots?: number;
	numberOfRoads?: number;

	// Loot
	numberOfMedikits?: number;
	numberOfLootBoxes?: number;
	numberOfMetalLootBoxes?: number;
	numberOfKeys?: number;
	numberOfBigContainers?: number;

	// Level director
	useLevelDirector?: boolean;
	levelDirectorOnlySpawnCaves?: boolean;

	// Exit
	generateExitToCaves?: boolean;
	exitLevelNumber?: number;
	arrivalSide?: Direction;
	exitSide?: Direction | null; // null = roll a random exit side at generation time

	// Portal config
	// Optional mob a portal may spawn instead of the standard spawner config.
	portalSpecialMob?: { mobType: string; chance: number } | null;
	reinforcedPortalChance?: number;

	// Generation config
	mobGenerationConfig?: MobGenerationConfig[];
	mobSpawnerConfig?: MobSpawnerConfig | null;
	lootTable?: LootTable | null;
	metalLootTable?: LootTable | null;
	vendingTable?: VendingTable | null;
	requiredPrefabs?: Prefab[];
	rewardLevel?: number;

	accept?: LevelAcceptanceCriteria;
}

const DEFAULTS: Required<Omit<WastelandLevelParams, "levelNumber" | "accept">> = {
	rng: Math.random,
	numberOfPortals: 0,
	numberOfFlavourPrefabs: 0,
	numberOfBigSquids: 0,
	numberOfArtilleryRobots: 0,
	numberOfRoads: 0,
	numberOfMedikits: 0,
	numberOfLootBoxes: 0,
	numberOfMetalLootBoxes: 0,
	numberOfKeys: 0,
	numberOfBigContainers: 0,
	useLevelDirector: false,
	levelDirectorOnlySpawnCaves: false,
	generateExitToCaves: false,
	exitLevelNumber: 0,
	arrivalSide: Direction.E,
	exitSide: null,
	portalSpecialMob: null,
	reinforcedPortalChance: 0,
	mobGenerationConfig: [],
	mobSpawnerConfig: null,
	lootTable: null,
	metalLootTable: null,
	vendingTable: null,
	requiredPrefabs: [],
	rewardLevel: 0,
};

type ResolvedParams = typeof DEFAULTS & { levelNumber: number };

export function generateWastelandLevel(params: WastelandLevelParams): Board {
	const p: ResolvedParams = { ...DEFAULTS, ...params };
	return generateWithRetries(p.levelNumber, () => runGeneration(p), params.accept);
}

function runGeneration(p: ResolvedParams): Board {
	const rng = p.rng;
	const arrivalSide = p.arrivalSide;

	const board = new Board(BOARD_WIDTH, BOARD_HEIGHT, rng);
	board.defaultFloor = "BrownFloor";
	board.areaName = Strings.get("area_wasteland") ?? "Wasteland";

	const reservedPositions = new Set<string>();
	const levelChangerXYs = new Set<string>();

	// Arrival
	const arrivalPoint = getRandomXYCloseToSide(board, arrivalSide, 2, 3, rng);
	levelChangerXYs.add(arrivalPoint.toString());
	if (p.levelNumber > 1) {
		board.addThing(new LevelUp(arrivalPoint, p.levelNumber - 1));
	}

	// Exit
	if (p.generateExitToCaves) {
		const prefab = getCaveDescent(p.exitLevelNumber);
		prefab.rotate(randomInt(0, 3, rng));
		const areaAvailableForExit = board.getAllXYs().filter((xy) => xy.distanceTo(arrivalPoint) >= 12);
		const xys = putPrefabOnArea(board, areaAvailableForExit, prefab, rng);
		if (!xys) throw new LevelGeneratorScrewUp("Failed to place cave descent prefab");
		for (const xy of xys) reservedPositions.add(xy.toString());
	} else {
		// With a fixed exitSide, the loop only re-rolls the exit point until it's far enough.
		let exitPointCandidate: XY;
		const maxExitAttempts = 200;
		let exitAttempt = 0;
		do {
			exitAttempt++;
			if (exitAttempt > maxExitAttempts) {
				throw new LevelGeneratorScrewUp("Failed to place exit far enough from arrival");
			}
			const exitSide = p.exitSide ?? cycleDirection(arrivalSide, randomInt(1, 3, rng) * 2);
			exitPointCandidate = getRandomXYCloseToSide(board, exitSide, 2, 3, rng);
		} while (arrivalPoint.distanceTo(exitPointCandidate) < 12);
		board.addThing(new LevelDown(exitPointCandidate, p.exitLevelNumber));
		levelChangerXYs.add(exitPointCandidate.toString());
	}

	// Reserve level changer positions
	for (const key of levelChangerXYs) reservedPositions.add(key);

	// Roads
	const roads = new Set<string>();
	for (let i = 0; i < p.numberOfRoads; i++) {
		const roadXYs = drawRoad(board, randomInt(2, 3, rng), randomChance(0.5, rng), rng);
		for (const xy of roadXYs) roads.add(xy.toString());
	}
	for (const key of roads) {
		const xy = XY.fromString(key);
		board.addThing(new ConcreteFloor(xy));
	}

	// Portals
	const areaForPortals = board.getAllXYs().filter((xy) => xy.distanceTo(arrivalPoint) > 6 && board.isCellEmpty(xy));
	for (let i = 0; i < p.numberOfPortals; i++) {
		const isReinforced = randomChance(p.reinforcedPortalChance, rng);
		const portal = placeRandomPortal(areaForPortals, board, rng, isReinforced);
		if (!portal) continue;

		if (p.portalSpecialMob && randomChance(p.portalSpecialMob.chance, rng)) {
			portal.addConfig({ mobType: p.portalSpecialMob.mobType, share: 1 });
		} else if (p.mobSpawnerConfig) {
			portal.setMobSpawnerConfig(p.mobSpawnerConfig);
		}
	}

	// Rocks/mountains — terrain variation
	const notNearLevelChangers = (xy: XY): boolean => {
		for (const key of levelChangerXYs) {
			if (xy.distanceTo(XY.fromString(key)) <= 3) return false;
		}
		return true;
	};
	const rockAndPlantsAvailablePositions = (xy: XY): boolean =>
		!reservedPositions.has(xy.toString()) &&
		notNearLevelChangers(xy) &&
		!roads.has(xy.toString()) &&
		!board.getThingsAt(xy).some((t) => t instanceof Plant) &&
		board.isPassable(xy);

	const borderRockBias = (xy: XY): number => {
		const dist = Math.min(xy.x, xy.y, board.width - 1 - xy.x, board.height - 1 - xy.y);
		if (dist >= BORDER_ROCK_BIAS_MARGIN) return 0;
		const t = 1 - dist / BORDER_ROCK_BIAS_MARGIN;
		return BORDER_ROCK_BIAS_STRENGTH * Math.pow(t, BORDER_ROCK_BIAS_EXPONENT);
	};

	// Map variations
	if (randomChance(0.4, rng)) {
		// Default
		applySimplexNoise(board, 3, 0.7, () => new Rock(new XY(0, 0)), rng, rockAndPlantsAvailablePositions, borderRockBias);
		if (randomChance(0.2, rng)) {
			// More plants
			applySimplexNoise(board, 2, 0.76, () => new Plant(new XY(0, 0)), rng, rockAndPlantsAvailablePositions);
		}
	} else {
		if (randomChance(0.5, rng)) {
			// Heavy mountains
			applySimplexNoise(
				board,
				3.5,
				0.6,
				() => new Rock(new XY(0, 0)),
				rng,
				rockAndPlantsAvailablePositions,
				borderRockBias,
			);
		} else {
			// Maze like
			applySimplexNoise(
				board,
				4,
				0.65,
				() => new Rock(new XY(0, 0)),
				rng,
				rockAndPlantsAvailablePositions,
				borderRockBias,
			);
		}
	}

	// Required prefabs
	let areaForPrefabs = board
		.getAllXYs()
		.filter(
			(xy) =>
				!reservedPositions.has(xy.toString()) &&
				!roads.has(xy.toString()) &&
				board.isCellEmpty(xy) &&
				xy.x > 0 &&
				xy.x < board.width - 1 &&
				xy.y > 0 &&
				xy.y < board.height - 1,
		);

	const mustPlacePrefabs: Prefab[] = [...p.requiredPrefabs];
	for (let i = 0; i < p.numberOfBigContainers; i++) {
		mustPlacePrefabs.push(getBigContainerPrefab());
	}

	for (let index = 0; index < mustPlacePrefabs.length; index++) {
		const prefab = mustPlacePrefabs[index];
		prefab.rotate(randomInt(0, 3, rng));
		const xys = putPrefabOnArea(board, areaForPrefabs, prefab, rng);
		if (!xys) {
			console.log(`Failed to place prefab ${index} of ${mustPlacePrefabs.length}`);
			throw new LevelGeneratorScrewUp(`Failed to place prefab ${index} of ${mustPlacePrefabs.length}`);
		}
		const placedSet = new Set(xys.map((xy) => xy.toString()));
		for (const xy of xys) reservedPositions.add(xy.toString());
		areaForPrefabs = areaForPrefabs.filter((xy) => !placedSet.has(xy.toString()));
	}

	// Flavour prefabs (do not fail level generation when there is no room)
	for (let i = 0; i < p.numberOfFlavourPrefabs; i++) {
		const prefab = getRandomFlavourPrefab(rng);
		prefab.rotate(randomInt(0, 3, rng));
		const xys = putPrefabOnArea(board, areaForPrefabs, prefab, rng);
		if (!xys) continue;
		const placedSet = new Set(xys.map((xy) => xy.toString()));
		for (const xy of xys) reservedPositions.add(xy.toString());
		areaForPrefabs = areaForPrefabs.filter((xy) => !placedSet.has(xy.toString()));
	}

	// Plants
	if (randomChance(0.8, rng)) {
		applySimplexNoise(board, 5, 0.9, () => new Plant(new XY(0, 0)), rng, rockAndPlantsAvailablePositions);
	}
	// Rocks
	if (randomChance(0.8, rng)) {
		applySimplexNoise(
			board,
			5,
			0.9,
			() => new DestroyedRock(new XY(0, 0)),
			rng,
			(xy) => board.isCellEmpty(xy),
			borderRockBias,
		);
	}

	// Mobs
	const canSpawnMobHere = (xy: XY): boolean =>
		xy.isInside(board) &&
		board.isPassable(xy) &&
		!board.getThingsAt(xy).some((t) => t instanceof LevelChangerThing) &&
		!board.hasMob(xy) &&
		arrivalPoint.distanceTo(xy) > 8 &&
		!reservedPositions.has(xy.toString());
	spawnMobsWithNoise(board, p.mobGenerationConfig, rng, canSpawnMobHere, 100);

	// Loot
	const boardCenterXY = new XY(Math.floor((board.width - 1) / 2), Math.floor((board.height - 1) / 2));
	const canSpawnLootHere = (xy: XY): boolean =>
		xy.isInside(board) &&
		!xy.isInBorder(board) &&
		board.isPassable(xy) &&
		!board.hasMob(xy) &&
		!board.getThingsAt(xy).some((t) => t instanceof LevelChangerThing) &&
		!board.getThingsAt(xy).some((t) => t instanceof Plant) &&
		!board.getThingsAt(xy).some((t) => t instanceof LootBox) &&
		!reservedPositions.has(xy.toString()) &&
		xy.distanceTo(boardCenterXY) <= 15;

	spawnThings(board, () => new LootBox(new XY(0, 0)), p.numberOfLootBoxes, rng, canSpawnLootHere);
	spawnThings(board, () => new MetalLootBox(new XY(0, 0)), p.numberOfMetalLootBoxes, rng, canSpawnLootHere);
	spawnThings(board, () => new MedicalLootBox(new XY(0, 0)), p.numberOfMedikits, rng, canSpawnLootHere);
	spawnThings(board, () => new KeyLootBox(new XY(0, 0)), p.numberOfKeys, rng, canSpawnLootHere);

	// Level Director
	if (p.useLevelDirector) {
		const levelDirector = new LevelDirector();
		if (p.mobSpawnerConfig) {
			levelDirector.setMobSpawnerConfig(p.mobSpawnerConfig);
		}
		levelDirector.onlySpawnCaves = p.levelDirectorOnlySpawnCaves;
		board.addNonPhysicalThing(levelDirector);
	}

	// Big Squids
	for (let i = 0; i < p.numberOfBigSquids; i++) {
		for (let attempt = 0; attempt < 100; attempt++) {
			const xy = getRandomXYByCriteriaInArea(
				board.getAllXYs(),
				(pos) => board.isCellEmpty(pos) && pos.distanceTo(arrivalPoint) > 12,
				rng,
			);
			if (!xy) break;
			if (xy.distanceTo(arrivalPoint) < 6) continue;
			if (BigSquid.create(board, xy)) break;
		}
	}

	// Artillery Robots
	for (let i = 0; i < p.numberOfArtilleryRobots; i++) {
		placeNxNMultiTileThing(
			() => new ArtilleryRobot(),
			board,
			2,
			rng,
			(xy) => board.isPassable(xy) && !board.hasMob(xy) && xy.distanceTo(arrivalPoint) > 12,
		);
	}

	populateLootBoxes(board, p.lootTable, p.metalLootTable, p.rewardLevel, rng);
	if (p.vendingTable) {
		populateVendingMachines(board, p.vendingTable, rng);
	}

	// Reachability check — early levels where player can't dig through terrain
	if (p.levelNumber <= 3) {
		const reachable = new Set(
			breadthFirstSearch(arrivalPoint, (xy) => xy.isInside(board) && board.isPassable(xy)).map((xy) => xy.toString()),
		);

		for (const thing of board.getPhysicalThings()) {
			if (thing instanceof LevelChangerThing && !reachable.has(thing.xy.toString())) {
				throw new LevelGeneratorScrewUp("Exit not reachable from arrival point");
			}
		}
	}

	if (p.levelNumber === 1) {
		board.initialSpawnXY = arrivalPoint;
	}
	assignGoldToPrePlacedMobs(board);
	board.dynamicGoldBudget = 300;
	return board;
}

function getRandomXYCloseToSide(
	board: Board,
	side: Direction,
	minDistFromCorners: number,
	maxDistFromBorders: number,
	rng: () => number,
): XY {
	const maxX = board.width - 1;
	const maxY = board.height - 1;
	const anyX = randomInt(minDistFromCorners, maxX - minDistFromCorners, rng);
	const anyY = randomInt(minDistFromCorners, maxY - minDistFromCorners, rng);

	if (side === Direction.E) {
		return new XY(randomInt(1, maxDistFromBorders, rng), anyY);
	} else if (side === Direction.W) {
		return new XY(randomInt(maxX - maxDistFromBorders, maxX - 1, rng), anyY);
	} else if (side === Direction.N) {
		return new XY(anyX, randomInt(maxY - maxDistFromBorders, maxY - 1, rng));
	} else if (side === Direction.S) {
		return new XY(anyX, randomInt(1, maxDistFromBorders, rng));
	}

	throw new Error("Invalid side: " + side);
}

export function drawRoad(board: Board, width: number, vertical: boolean, rng: () => number = Math.random): XY[] {
	const directionChain = new MarkovChain<number>();
	directionChain.addConfig(-1, -1, 50);
	directionChain.addConfig(-1, 0, 50);
	directionChain.addConfig(-1, 1, 0);

	directionChain.addConfig(0, 0, 80);
	directionChain.addConfig(0, -1, 10);
	directionChain.addConfig(0, 1, 10);

	directionChain.addConfig(1, 1, 50);
	directionChain.addConfig(1, 0, 50);
	directionChain.addConfig(1, -1, 0);

	const xys: XY[] = [];
	let x = 0;
	const crossAxisMax = vertical ? board.width - 1 : board.height - 1;
	const margin = Math.ceil(width / 2) + 1;
	let y = randomInt(margin, crossAxisMax - margin, rng);
	let currentDirection = randomInt(-1, 1, rng);

	const maxX = vertical ? board.height - 1 : board.width - 1;

	while (x <= maxX) {
		for (let roadY = y - Math.floor(width / 2); roadY <= y + Math.floor(width / 2); roadY++) {
			const roadXY = vertical ? new XY(roadY, x) : new XY(x, roadY);
			if (!roadXY.isInside(board)) continue;
			xys.push(roadXY);
		}
		x++;
		y = y + currentDirection;
		y = Math.max(margin, Math.min(crossAxisMax - margin, y));
		currentDirection = directionChain.run(currentDirection, rng);
	}

	return xys;
}

function getRandomFlavourPrefab(rng: () => number): Prefab {
	const suppliers = [getSmallBloodTemple, getBloodTemple, getGasBarrelsWithFence, getBuilding1, getMobFortress1];
	return suppliers[randomInt(0, suppliers.length - 1, rng)]();
}
